import { Fragment, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Calculator,
  Coins,
  Download,
  Euro,
  Info,
  Minus,
  PencilLine,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { api, getApiError } from "../api";
import {
  Button,
  Drawer,
  Field,
  InlineError,
  Input,
  Modal,
  Panel,
  Select,
  Skeleton,
  StatePanel,
  formatDate,
  formatMoney,
} from "../components/ui";
import { useAsync, useDebouncedValue } from "../hooks";
import type { PageContext } from "../App";
import type {
  FinanceAlert,
  FinanceOverview,
  ProfitPreviewResult,
  SkuCostEntry,
  SkuProfitItem,
} from "../types";
import { MARKETPLACE_CODES } from "../types";

type FinanceTab = "overview" | "skus" | "events" | "alerts";

const PERIOD_OPTIONS = [
  { value: 7, label: "7 Tage" },
  { value: 30, label: "30 Tage" },
  { value: 90, label: "90 Tage" },
  { value: 180, label: "180 Tage" },
  { value: 365, label: "12 Monate" },
];

const EVENT_TYPE_OPTIONS = [
  { value: "", label: "Alle Typen" },
  { value: "shipment", label: "Bestellungen" },
  { value: "refund", label: "Erstattungen" },
  { value: "chargeback", label: "Chargebacks" },
  { value: "guarantee_claim", label: "A-bis-Z-Garantie" },
  { value: "service_fee", label: "Servicegebühren" },
  { value: "product_ads", label: "Werbekosten" },
  { value: "adjustment", label: "Anpassungen" },
  { value: "coupon", label: "Coupons" },
  { value: "deal", label: "Angebotsaktionen" },
  { value: "removal", label: "Remissionen" },
  { value: "liquidation", label: "Liquidationen" },
  { value: "safet_reimbursement", label: "SAFE-T-Erstattungen" },
  { value: "unknown", label: "Nicht bewertet" },
];

function signedMoney(value?: number | null, currency = "EUR"): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "–";
  const formatted = formatMoney(Math.abs(value), currency);
  if (value > 0.004) return `+${formatted}`;
  if (value < -0.004) return `-${formatted}`;
  return formatted;
}

function moneyTone(value?: number | null): "positive" | "danger" | "neutral" {
  if (typeof value !== "number" || Number.isNaN(value)) return "neutral";
  if (value > 0.004) return "positive";
  if (value < -0.004) return "danger";
  return "neutral";
}

function KpiCard({
  label,
  value,
  delta,
  detail,
}: {
  label: string;
  value: number;
  delta?: number | null;
  detail?: string;
}) {
  const deltaKnown = typeof delta === "number" && Number.isFinite(delta);
  // All KPIs are signed (costs are negative), so a rising signed value is
  // always an improvement: fees moving toward zero mean lower costs.
  const improving = deltaKnown ? delta > 0 : null;
  return (
    <div className={`finance-kpi finance-kpi--${moneyTone(value)}`}>
      <span className="finance-kpi__label">{label}</span>
      <strong className="finance-kpi__value">{formatMoney(value)}</strong>
      <span className="finance-kpi__meta">
        {deltaKnown ? (
          <span
            className={`finance-trend finance-trend--${
              improving ? "up" : "down"
            }`}
          >
            {delta! > 0 ? <ArrowUpRight size={13} /> : delta! < 0 ? <ArrowDownRight size={13} /> : <Minus size={13} />}
            {Math.abs(delta!).toFixed(1)} %
          </span>
        ) : (
          <span className="finance-trend finance-trend--neutral">
            <Minus size={13} /> Vorperiode ohne Basis
          </span>
        )}
        {detail && <small>{detail}</small>}
      </span>
    </div>
  );
}

function DailyChart({ overview }: { overview: FinanceOverview }) {
  const scale = useMemo(() => {
    let max = 0;
    for (const point of overview.series) {
      max = Math.max(max, point.sales, Math.abs(point.costs) + Math.abs(point.refunds));
    }
    return max || 1;
  }, [overview.series]);
  if (!overview.series.length) {
    return <StatePanel type="empty" compact title="Keine Buchungen im Zeitraum" />;
  }
  return (
    <div className="finance-chart" role="img" aria-label="Umsatz und Kosten pro Tag">
      {overview.series.map((point) => {
        const costs = Math.abs(point.costs) + Math.abs(point.refunds);
        return (
          <div
            key={point.date}
            className="finance-chart__col"
            title={`${formatDate(point.date)}\nUmsatz ${formatMoney(point.sales)}\nGebühren/Kosten ${formatMoney(
              point.costs,
            )}\nErstattungen ${formatMoney(point.refunds)}\nNetto ${formatMoney(point.net)}`}
          >
            <span className="finance-chart__up">
              <span style={{ height: `${(point.sales / scale) * 100}%` }} />
            </span>
            <span className="finance-chart__down">
              <span style={{ height: `${(costs / scale) * 100}%` }} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

function FeeTypesPanel({ overview }: { overview: FinanceOverview }) {
  const items = overview.feeTypes.slice(0, 12);
  const max = Math.max(...items.map((item) => Math.abs(item.amount)), 1);
  return (
    <Panel className="finance-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Amazon-Gebühren</p>
          <h2>Zusammensetzung nach Gebührenart</h2>
          <p className="muted">
            Alle Gebührenpositionen ohne Werbung, inklusive Gebühren-Gutschriften aus
            Erstattungen.
          </p>
        </div>
        <Coins size={20} />
      </div>
      {items.length === 0 ? (
        <StatePanel type="empty" compact title="Keine Gebühren im Zeitraum" />
      ) : (
        <ul className="finance-bar-list">
          {items.map((item) => (
            <li key={item.type}>
              <span className="finance-bar-list__label" title={item.type}>
                {item.type}
                <small>{item.count}×</small>
              </span>
              <span className="finance-bar-list__track">
                <span
                  className="finance-bar-list__bar"
                  style={{ width: `${(Math.abs(item.amount) / max) * 100}%` }}
                />
              </span>
              <span className="finance-bar-list__value">{formatMoney(item.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function FinancePage({ navigate, notify }: PageContext) {
  void navigate;
  const [tab, setTab] = useState<FinanceTab>("overview");
  const [days, setDays] = useState(30);
  const [marketplace, setMarketplace] = useState("");
  const [syncing, setSyncing] = useState(false);

  const overview = useAsync(
    () => api.financeOverview({ days, marketplace: marketplace || undefined }),
    [days, marketplace],
  );
  const alerts = useAsync(() => api.financeAlerts(days), [days]);

  const [skuSort, setSkuSort] = useState("sales");
  const [skuQuery, setSkuQuery] = useState("");
  const debouncedSkuQuery = useDebouncedValue(skuQuery);
  const skus = useAsync(
    () =>
      api.financeSkus({
        days,
        marketplace: marketplace || undefined,
        q: debouncedSkuQuery || undefined,
        sort: skuSort,
      }),
    [days, marketplace, debouncedSkuQuery, skuSort],
  );

  const [eventType, setEventType] = useState("");
  const [eventQuery, setEventQuery] = useState("");
  const debouncedEventQuery = useDebouncedValue(eventQuery);
  const [eventPages, setEventPages] = useState(1);
  const events = useAsync(
    () =>
      api.financeEvents({
        days,
        marketplace: marketplace || undefined,
        type: eventType || undefined,
        q: debouncedEventQuery || undefined,
        limit: 100 * eventPages,
      }),
    [days, marketplace, eventType, debouncedEventQuery, eventPages],
  );
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  const [costTarget, setCostTarget] = useState<SkuProfitItem | null>(null);
  const [calculatorTarget, setCalculatorTarget] = useState<SkuProfitItem | null>(null);

  const startSync = async () => {
    setSyncing(true);
    try {
      const result = await api.startFinanceSync({});
      notify(
        result.reused
          ? "Ein Finanz-Abgleich läuft bereits."
          : `Finanz-Abgleich gestartet · Job ${result.jobId}`,
        result.reused ? "info" : "success",
      );
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setSyncing(false);
    }
  };

  const alertCounts = alerts.data?.counts;
  const sync = overview.data?.sync;

  return (
    <div className="finance-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Amazon Geldflüsse</p>
          <h1>Finanzen & Profit</h1>
          <p className="page-description">
            Umsätze, Erstattungen, sämtliche Amazon-Gebühren und Werbekosten aus der
            Finances-API – zusammengeführt mit deinen Einkaufskosten zum echten Gewinn je Artikel.
          </p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon={<RefreshCw size={16} />}
            loading={syncing}
            onClick={startSync}
          >
            Finanzdaten abgleichen
          </Button>
        </div>
      </header>

      <div className="finance-toolbar">
        <div className="finance-toolbar__filters">
          <Select
            aria-label="Zeitraum"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Marktplatz"
            value={marketplace}
            onChange={(event) => setMarketplace(event.target.value)}
          >
            <option value="">Alle Marktplätze</option>
            {MARKETPLACE_CODES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </div>
        <span className="finance-toolbar__sync">
          {sync?.lastSyncAt
            ? `Letzter Abgleich ${formatDate(sync.lastSyncAt)} · ${sync.totalEvents ?? 0} Buchungen`
            : "Noch kein Finanz-Abgleich ausgeführt."}
        </span>
      </div>

      <div className="finance-tabs" role="tablist" aria-label="Finanzbereiche">
        {(
          [
            ["overview", "Übersicht"],
            ["skus", "Artikel-Profit"],
            ["events", "Buchungen"],
            ["alerts", "Warnungen"],
          ] as Array<[FinanceTab, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            className={tab === key ? "is-active" : ""}
            onClick={() => setTab(key)}
          >
            {label}
            {key === "alerts" && alertCounts && alertCounts.critical + alertCounts.warning > 0 && (
              <small className="finance-tabs__badge">
                {alertCounts.critical + alertCounts.warning}
              </small>
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab overview={overview.data} loading={overview.loading} error={overview.error} onRetry={overview.reload} />
      )}

      {tab === "skus" && (
        <Panel className="finance-panel">
          <div className="finance-panel__toolbar">
            <Input
              placeholder="SKU, ASIN oder Titel suchen …"
              value={skuQuery}
              onChange={(event) => setSkuQuery(event.target.value)}
              aria-label="Artikel suchen"
            />
            <Select
              aria-label="Sortierung"
              value={skuSort}
              onChange={(event) => setSkuSort(event.target.value)}
            >
              <option value="sales">Nach Umsatz</option>
              <option value="profit">Nach Gewinn</option>
              <option value="margin">Nach Marge (aufsteigend)</option>
              <option value="units">Nach Einheiten</option>
              <option value="refunds">Nach Erstattungen</option>
              <option value="fees">Nach Gebühren</option>
            </Select>
          </div>
          {skus.loading ? (
            <Skeleton lines={6} />
          ) : skus.error ? (
            <InlineError message={skus.error} onRetry={() => void skus.reload()} />
          ) : !skus.data || skus.data.items.length === 0 ? (
            <StatePanel
              type="empty"
              title="Keine Verkaufsdaten im Zeitraum"
              description="Nach dem ersten Finanz-Abgleich erscheinen hier Umsatz, Gebühren und Gewinn je Artikel."
            />
          ) : (
            <>
              <div className="finance-summary-row">
                <span>
                  <strong>{skus.data.summary.skuCount}</strong> Artikel ·{" "}
                  <strong>{skus.data.summary.unitsSold}</strong> Einheiten
                </span>
                <span>
                  Umsatz <strong>{formatMoney(skus.data.summary.sales)}</strong> · Gebühren{" "}
                  <strong>{formatMoney(skus.data.summary.fees)}</strong> · Netto{" "}
                  <strong>{formatMoney(skus.data.summary.netProceeds)}</strong>
                </span>
                {skus.data.summary.missingCostCount > 0 && (
                  <span className="finance-summary-row__warning">
                    <AlertTriangle size={14} /> {skus.data.summary.missingCostCount} Artikel ohne
                    Einkaufskosten
                  </span>
                )}
              </div>
              <div className="finance-table-wrap">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>Artikel</th>
                      <th>Einheiten</th>
                      <th>Umsatz</th>
                      <th>Erstattet</th>
                      <th>Gebühren</th>
                      <th>Netto</th>
                      <th>Eigenkosten</th>
                      <th>Gewinn</th>
                      <th>Marge</th>
                      <th aria-label="Aktionen" />
                    </tr>
                  </thead>
                  <tbody>
                    {skus.data.items.map((item) => (
                      <tr key={item.sellerSku}>
                        <td className="finance-table__product">
                          <span title={item.title}>{item.sellerSku}</span>
                          <small title={item.title}>
                            {item.title || "–"}
                            {item.marketplaces.length > 0 && ` · ${item.marketplaces.join(", ")}`}
                          </small>
                        </td>
                        <td>
                          {item.unitsSold}
                          {item.unitsRefunded > 0 && (
                            <small className="finance-table__sub">-{item.unitsRefunded} ret.</small>
                          )}
                        </td>
                        <td>{formatMoney(item.sales)}</td>
                        <td className={item.refunds < -0.004 ? "is-negative" : ""}>
                          {signedMoney(item.refunds)}
                        </td>
                        <td className="is-negative">{signedMoney(item.fees)}</td>
                        <td>{formatMoney(item.netProceeds)}</td>
                        <td>
                          {item.unitCost != null ? (
                            `${formatMoney(item.unitCost)}/Stk.`
                          ) : (
                            <span className="finance-flag finance-flag--warning">fehlt</span>
                          )}
                        </td>
                        <td
                          className={
                            item.profit == null ? "" : item.profit < 0 ? "is-negative" : "is-positive"
                          }
                        >
                          {item.profit != null ? signedMoney(item.profit) : "unbekannt"}
                        </td>
                        <td>
                          {item.marginPercent != null ? (
                            <span
                              className={`finance-flag ${
                                item.marginPercent < 0
                                  ? "finance-flag--danger"
                                  : item.marginPercent < 10
                                    ? "finance-flag--warning"
                                    : "finance-flag--positive"
                              }`}
                            >
                              {item.marginPercent.toFixed(1)} %
                            </span>
                          ) : (
                            "–"
                          )}
                        </td>
                        <td className="finance-table__actions">
                          <button
                            className="text-button"
                            onClick={() => setCostTarget(item)}
                            title="Einkaufskosten pflegen"
                          >
                            <PencilLine size={15} /> Kosten
                          </button>
                          <button
                            className="text-button"
                            onClick={() => setCalculatorTarget(item)}
                            title="Gewinn- und Break-even-Rechner öffnen"
                          >
                            <Calculator size={15} /> Rechner
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Panel>
      )}

      {tab === "events" && (
        <Panel className="finance-panel">
          <div className="finance-panel__toolbar">
            <Input
              placeholder="SKU, Bestellnummer oder Beschreibung …"
              value={eventQuery}
              onChange={(event) => {
                setEventQuery(event.target.value);
                setEventPages(1);
              }}
              aria-label="Buchungen durchsuchen"
            />
            <Select
              aria-label="Buchungstyp"
              value={eventType}
              onChange={(event) => {
                setEventType(event.target.value);
                setEventPages(1);
              }}
            >
              {EVENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <a
              className="button button--secondary finance-export-link"
              href={api.financeEventsExportUrl({
                days,
                marketplace: marketplace || undefined,
                type: eventType || undefined,
                q: debouncedEventQuery || undefined,
              })}
              download
            >
              <Download size={16} />
              <span>CSV exportieren</span>
            </a>
          </div>
          {events.loading && !events.data ? (
            <Skeleton lines={6} />
          ) : events.error ? (
            <InlineError message={events.error} onRetry={() => void events.reload()} />
          ) : !events.data || events.data.items.length === 0 ? (
            <StatePanel
              type="empty"
              title="Keine Buchungen gefunden"
              description="Zeitraum vergrößern oder zuerst einen Finanz-Abgleich starten."
            />
          ) : (
            <>
              <div className="finance-table-wrap">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Typ</th>
                      <th>Zuordnung</th>
                      <th>Markt</th>
                      <th>Umsatzanteil</th>
                      <th>Gebühren</th>
                      <th>Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.data.items.map((item) => (
                      <Fragment key={item.id}>
                        <tr
                          className="finance-table__row-button"
                          onClick={() =>
                            setExpandedEventId(expandedEventId === item.id ? null : item.id)
                          }
                        >
                          <td>
                            {formatDate(item.postedAt ?? undefined)}
                            {item.postedAtEstimated && (
                              <small className="finance-table__sub" title="Amazon liefert für diesen Ereignistyp kein Buchungsdatum; angezeigt wird das Abrufdatum.">
                                ≈ geschätzt
                              </small>
                            )}
                          </td>
                          <td>{item.typeLabel}</td>
                          <td className="finance-table__product">
                            <span>{item.sellerSku || item.amazonOrderId || "Konto"}</span>
                            <small title={item.description}>
                              {item.sellerSku && item.amazonOrderId
                                ? item.amazonOrderId
                                : item.description || ""}
                            </small>
                          </td>
                          <td>{item.marketplace ?? "–"}</td>
                          <td>{item.revenue ? signedMoney(item.revenue + item.promotions) : "–"}</td>
                          <td className={item.fees < -0.004 ? "is-negative" : ""}>
                            {item.fees ? signedMoney(item.fees) : "–"}
                          </td>
                          <td className={item.total < -0.004 ? "is-negative" : "is-positive"}>
                            {signedMoney(item.total)}
                          </td>
                        </tr>
                        {expandedEventId === item.id && (
                          <tr className="finance-table__detail-row">
                            <td colSpan={7}>
                              <ul className="finance-breakdown">
                                {item.breakdown.length === 0 && (
                                  <li>
                                    <span>Keine Einzelpositionen vorhanden.</span>
                                  </li>
                                )}
                                {item.breakdown.map((part, index) => (
                                  <li key={`${item.id}-${index}`}>
                                    <span>{part.type}</span>
                                    <small>{part.kind}</small>
                                    <strong className={part.amount < 0 ? "is-negative" : ""}>
                                      {signedMoney(part.amount, item.currency)}
                                    </strong>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="finance-panel__footer">
                <span>
                  {events.data.items.length} von {events.data.total} Buchungen
                </span>
                {events.data.items.length < events.data.total && (
                  <Button
                    variant="secondary"
                    loading={events.loading}
                    onClick={() => setEventPages((current) => current + 1)}
                  >
                    Mehr laden
                  </Button>
                )}
              </div>
            </>
          )}
        </Panel>
      )}

      {tab === "alerts" && (
        <Panel className="finance-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Automatische Prüfung</p>
              <h2>Was kostet dich gerade Geld?</h2>
            </div>
            <ShieldAlert size={20} />
          </div>
          {alerts.loading ? (
            <Skeleton lines={4} />
          ) : alerts.error ? (
            <InlineError message={alerts.error} onRetry={() => void alerts.reload()} />
          ) : !alerts.data || alerts.data.items.length === 0 ? (
            <StatePanel
              type="empty"
              title="Keine Auffälligkeiten"
              description="Margen, Gebührenquote und Erstattungen liegen im Rahmen."
            />
          ) : (
            <ul className="finance-alert-list">
              {alerts.data.items.map((alert, index) => (
                <FinanceAlertRow
                  key={`${alert.kind}-${alert.sellerSku ?? index}`}
                  alert={alert}
                  onEditCosts={
                    alert.sellerSku
                      ? () => {
                          const item = skus.data?.items.find(
                            (entry) => entry.sellerSku === alert.sellerSku,
                          );
                          setCostTarget(
                            item ?? {
                              sellerSku: alert.sellerSku!,
                              title: "",
                              marketplaces: [],
                              unitsSold: 0,
                              unitsRefunded: 0,
                              sales: 0,
                              refunds: 0,
                              fees: 0,
                              other: 0,
                              netProceeds: 0,
                              flags: [],
                            },
                          );
                        }
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
        </Panel>
      )}

      <CostModal
        target={costTarget}
        onClose={() => setCostTarget(null)}
        onSaved={() => {
          setCostTarget(null);
          void skus.reload();
          void alerts.reload();
          notify("Einkaufskosten gespeichert.", "success");
        }}
        notify={notify}
      />

      <ProfitCalculatorDrawer
        target={calculatorTarget}
        onClose={() => setCalculatorTarget(null)}
        notify={notify}
      />
    </div>
  );
}

function OverviewTab({
  overview,
  loading,
  error,
  onRetry,
}: {
  overview: FinanceOverview | null;
  loading: boolean;
  error: string | null;
  onRetry: () => Promise<void>;
}) {
  if (loading) {
    return (
      <Panel className="finance-panel">
        <Skeleton lines={5} />
      </Panel>
    );
  }
  if (error) {
    return <InlineError message={error} onRetry={() => void onRetry()} />;
  }
  if (!overview) return null;
  const { kpis } = overview;
  const hasData = overview.eventTypes.length > 0;
  return (
    <>
      {!hasData && (
        <StatePanel
          type="empty"
          title="Noch keine Finanzdaten"
          description="Starte oben rechts den ersten Finanz-Abgleich, um Umsätze, Gebühren und Erstattungen von Amazon zu laden."
        />
      )}
      <section className="finance-kpi-grid">
        <KpiCard
          label="Umsatz"
          value={kpis.sales}
          delta={kpis.delta.sales}
          detail={`${overview.orders} Bestellungen · ${overview.unitsSold} Einheiten`}
        />
        <KpiCard
          label="Erstattungen"
          value={kpis.refunds}
          delta={kpis.delta.refunds}
          detail={`${overview.unitsRefunded} Einheiten zurück`}
        />
        <KpiCard label="Amazon-Gebühren" value={kpis.fees} delta={kpis.delta.fees} />
        <KpiCard label="Werbekosten" value={kpis.ads} delta={kpis.delta.ads} />
        <KpiCard label="Sonstiges" value={kpis.other} delta={kpis.delta.other} />
        <KpiCard
          label="Netto-Erlös"
          value={kpis.net}
          delta={kpis.delta.net}
          detail="vor Einkaufskosten"
        />
      </section>

      <div className="finance-grid-two">
        <Panel className="finance-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Verlauf</p>
              <h2>Umsatz und Kosten pro Tag</h2>
            </div>
            <Euro size={20} />
          </div>
          <DailyChart overview={overview} />
          <p className="finance-chart__legend">
            <span className="finance-chart__legend-up" /> Umsatz
            <span className="finance-chart__legend-down" /> Gebühren, Werbung & Erstattungen
          </p>
        </Panel>
        <FeeTypesPanel overview={overview} />
      </div>

      <div className="finance-grid-two">
        <Panel className="finance-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Marktplätze</p>
              <h2>Wo entsteht das Ergebnis?</h2>
            </div>
          </div>
          <div className="finance-table-wrap">
            <table className="finance-table">
              <thead>
                <tr>
                  <th>Markt</th>
                  <th>Umsatz</th>
                  <th>Gebühren</th>
                  <th>Netto</th>
                </tr>
              </thead>
              <tbody>
                {overview.marketplaces.map((item) => (
                  <tr key={item.code}>
                    <td>{item.code === "??" ? "Konto / ohne Zuordnung" : item.code}</td>
                    <td>{formatMoney(item.sales)}</td>
                    <td className="is-negative">{signedMoney(item.fees)}</td>
                    <td className={item.net < 0 ? "is-negative" : "is-positive"}>
                      {signedMoney(item.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
        <Panel className="finance-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Ereignistypen</p>
              <h2>Alle Buchungsarten im Zeitraum</h2>
            </div>
          </div>
          <div className="finance-table-wrap">
            <table className="finance-table">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Anzahl</th>
                  <th>Summe</th>
                </tr>
              </thead>
              <tbody>
                {overview.eventTypes.map((item) => (
                  <tr key={item.type}>
                    <td>
                      {item.label}
                      {item.type === "unknown" && (
                        <small
                          className="finance-table__sub"
                          title="Diese Amazon-Ereignisart wird gespeichert, aber noch nicht bewertet – der Betrag fließt bewusst nicht in die Kennzahlen ein."
                        >
                          <Info size={12} /> ohne Betrag
                        </small>
                      )}
                    </td>
                    <td>{item.count}</td>
                    <td className={item.total < 0 ? "is-negative" : ""}>
                      {signedMoney(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </>
  );
}

function FinanceAlertRow({
  alert,
  onEditCosts,
}: {
  alert: FinanceAlert;
  onEditCosts?: () => void;
}) {
  return (
    <li className={`finance-alert finance-alert--${alert.severity}`}>
      <span className="finance-alert__icon">
        {alert.severity === "critical" ? (
          <ShieldAlert size={18} />
        ) : alert.severity === "warning" ? (
          <AlertTriangle size={18} />
        ) : (
          <Info size={18} />
        )}
      </span>
      <span className="finance-alert__body">
        <strong>{alert.title}</strong>
        <span>{alert.message}</span>
      </span>
      {onEditCosts && alert.kind === "missing_cost" && (
        <Button variant="secondary" onClick={onEditCosts}>
          Kosten pflegen
        </Button>
      )}
    </li>
  );
}

function CostModal({
  target,
  onClose,
  onSaved,
  notify,
}: {
  target: SkuProfitItem | null;
  onClose: () => void;
  onSaved: () => void;
  notify: PageContext["notify"];
}) {
  const costs = useAsync(api.financeCosts, [target?.sellerSku], target != null);
  const existing: SkuCostEntry | undefined = costs.data?.find(
    (entry) => entry.sellerSku === target?.sellerSku,
  );
  const [form, setForm] = useState({
    purchaseCostNet: "",
    packagingCost: "",
    shippingCost: "",
    otherCost: "",
    note: "",
  });
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (target && !costs.loading && initializedFor !== target.sellerSku) {
    setForm({
      purchaseCostNet:
        existing?.purchaseCostNet != null ? String(existing.purchaseCostNet) : "",
      packagingCost: existing?.packagingCost ? String(existing.packagingCost) : "",
      shippingCost: existing?.shippingCost ? String(existing.shippingCost) : "",
      otherCost: existing?.otherCost ? String(existing.otherCost) : "",
      note: existing?.note ?? "",
    });
    setInitializedFor(target.sellerSku);
  }

  const parse = (value: string): number | null => {
    if (!value.trim()) return null;
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };

  const save = async () => {
    if (!target) return;
    const purchase = parse(form.purchaseCostNet);
    if (form.purchaseCostNet.trim() && purchase == null) {
      notify("Der Einkaufspreis ist keine gültige Zahl.", "error");
      return;
    }
    setSaving(true);
    try {
      await api.saveFinanceCost(target.sellerSku, {
        purchaseCostNet: purchase,
        packagingCost: parse(form.packagingCost) ?? 0,
        shippingCost: parse(form.shippingCost) ?? 0,
        otherCost: parse(form.otherCost) ?? 0,
        note: form.note,
      });
      setInitializedFor(null);
      onSaved();
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={target != null}
      title={`Einkaufskosten · ${target?.sellerSku ?? ""}`}
      description="Nettokosten je verkaufter Einheit. Sie fließen in Gewinn, Marge und Break-even ein."
      onClose={() => {
        setInitializedFor(null);
        onClose();
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              setInitializedFor(null);
              onClose();
            }}
          >
            Abbrechen
          </Button>
          <Button loading={saving} onClick={save}>
            Kosten speichern
          </Button>
        </>
      }
    >
      {costs.loading ? (
        <Skeleton lines={4} />
      ) : (
        <div className="form-grid--two">
          <Field label="Einkaufspreis netto (EUR)" hint="Ohne USt., pro Einheit">
            <Input
              inputMode="decimal"
              value={form.purchaseCostNet}
              onChange={(event) => setForm({ ...form, purchaseCostNet: event.target.value })}
              placeholder="z. B. 7,90"
            />
          </Field>
          <Field label="Verpackung (EUR)">
            <Input
              inputMode="decimal"
              value={form.packagingCost}
              onChange={(event) => setForm({ ...form, packagingCost: event.target.value })}
              placeholder="0,00"
            />
          </Field>
          <Field label="Versand je Einheit (EUR)" hint="Eigener FBM-Versand; bei FBA meist 0">
            <Input
              inputMode="decimal"
              value={form.shippingCost}
              onChange={(event) => setForm({ ...form, shippingCost: event.target.value })}
              placeholder="0,00"
            />
          </Field>
          <Field label="Sonstige Kosten (EUR)">
            <Input
              inputMode="decimal"
              value={form.otherCost}
              onChange={(event) => setForm({ ...form, otherCost: event.target.value })}
              placeholder="0,00"
            />
          </Field>
          <Field label="Notiz" className="form-grid__wide">
            <Input
              value={form.note}
              onChange={(event) => setForm({ ...form, note: event.target.value })}
              placeholder="z. B. Lieferant, Staffelpreis"
              maxLength={500}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
}

function ProfitCalculatorDrawer({
  target,
  onClose,
  notify,
}: {
  target: SkuProfitItem | null;
  onClose: () => void;
  notify: PageContext["notify"];
}) {
  const [marketplace, setMarketplace] = useState<string>("DE");
  const [price, setPrice] = useState("");
  const [targetMargin, setTargetMargin] = useState("10");
  const [result, setResult] = useState<ProfitPreviewResult | null>(null);
  const [computing, setComputing] = useState(false);
  const [refreshingFees, setRefreshingFees] = useState(false);
  const [initializedFor, setInitializedFor] = useState<string | null>(null);

  if (target && initializedFor !== target.sellerSku) {
    setMarketplace(target.marketplaces[0] ?? "DE");
    setPrice(target.currentPrice != null ? String(target.currentPrice) : "");
    setResult(null);
    setInitializedFor(target.sellerSku);
  }

  const compute = async (overrides: { refreshedFees?: boolean } = {}) => {
    if (!target) return;
    const parsedPrice = price.trim() ? Number(price.replace(",", ".")) : undefined;
    if (price.trim() && (!Number.isFinite(parsedPrice) || parsedPrice! <= 0)) {
      notify("Der Preis ist keine gültige Zahl.", "error");
      return;
    }
    const parsedMargin = targetMargin.trim() ? Number(targetMargin.replace(",", ".")) : undefined;
    setComputing(true);
    try {
      const preview = await api.financeProfitPreview({
        sellerSku: target.sellerSku,
        marketplace,
        price: parsedPrice ?? null,
        targetMarginPercent:
          parsedMargin != null && Number.isFinite(parsedMargin) ? parsedMargin : null,
      });
      setResult(preview);
      if (overrides.refreshedFees) {
        notify("Amazon-Gebühren aktualisiert und neu berechnet.", "success");
      }
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setComputing(false);
    }
  };

  const refreshFees = async () => {
    if (!target) return;
    setRefreshingFees(true);
    try {
      const response = await api.refreshFinanceFees({
        sellerSkus: [target.sellerSku],
        marketplaces: [marketplace],
      });
      const failed = response.items.find((item) => item.status !== "ok");
      if (failed) {
        notify(failed.message || "Amazon hat keine Gebührenschätzung geliefert.", "error");
      }
      await compute({ refreshedFees: !failed });
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setRefreshingFees(false);
    }
  };

  return (
    <Drawer
      open={target != null}
      title={`Gewinn-Rechner · ${target?.sellerSku ?? ""}`}
      subtitle={target?.title || undefined}
      onClose={() => {
        setInitializedFor(null);
        onClose();
      }}
    >
      <div className="finance-calculator">
        <div className="form-grid--two">
          <Field label="Marktplatz">
            <Select value={marketplace} onChange={(event) => setMarketplace(event.target.value)}>
              {MARKETPLACE_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Verkaufspreis brutto (EUR)" hint="Leer = aktueller Amazon-Preis">
            <Input
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              placeholder="z. B. 24,95"
            />
          </Field>
          <Field label="Zielmarge (%)" hint="Für die Mindestpreis-Empfehlung">
            <Input
              inputMode="decimal"
              value={targetMargin}
              onChange={(event) => setTargetMargin(event.target.value)}
            />
          </Field>
        </div>
        <div className="finance-calculator__actions">
          <Button loading={computing} icon={<Calculator size={16} />} onClick={() => compute()}>
            Berechnen
          </Button>
          <Button
            variant="secondary"
            loading={refreshingFees}
            icon={<RefreshCw size={16} />}
            onClick={refreshFees}
          >
            Gebühren bei Amazon abfragen
          </Button>
        </div>

        {result && (
          <div className="finance-calculator__result">
            <ul className="finance-result-list">
              <li>
                <span>Verkaufspreis ({result.marketplace})</span>
                <strong>{formatMoney(result.price, result.currency)}</strong>
              </li>
              <li>
                <span>Netto nach {result.vatRatePercent.toFixed(0)} % USt.</span>
                <strong>{formatMoney(result.netRevenue, result.currency)}</strong>
              </li>
              <li>
                <span>Amazon-Gebühren (geschätzt)</span>
                <strong className={result.feesEstimated != null ? "is-negative" : ""}>
                  {result.feesEstimated != null
                    ? `-${formatMoney(result.feesEstimated, result.currency)}`
                    : "unbekannt"}
                </strong>
              </li>
              <li>
                <span>Eigenkosten je Einheit</span>
                <strong className={result.unitCost != null ? "is-negative" : ""}>
                  {result.unitCost != null
                    ? `-${formatMoney(result.unitCost, result.currency)}`
                    : "nicht hinterlegt"}
                </strong>
              </li>
              <li className="finance-result-list__total">
                <span>Gewinn je Einheit</span>
                <strong
                  className={
                    result.profit == null ? "" : result.profit < 0 ? "is-negative" : "is-positive"
                  }
                >
                  {result.profit != null
                    ? `${signedMoney(result.profit, result.currency)} (${
                        result.marginPercent != null ? `${result.marginPercent.toFixed(1)} %` : "–"
                      })`
                    : "erst nach Gebührenabfrage"}
                </strong>
              </li>
              {result.breakEvenPrice != null && (
                <li>
                  <span>Break-even-Preis</span>
                  <strong>{formatMoney(result.breakEvenPrice, result.currency)}</strong>
                </li>
              )}
              {result.targetMarginPrice != null && (
                <li>
                  <span>Mindestpreis für {result.targetMarginPercent}% Marge</span>
                  <strong>{formatMoney(result.targetMarginPrice, result.currency)}</strong>
                </li>
              )}
            </ul>
            {result.feeEstimate && result.feeEstimate.status === "ok" && (
              <div className="finance-fee-detail">
                <p className="eyebrow">Gebührendetails von Amazon</p>
                <ul>
                  {result.feeEstimate.feeBreakdown.map((part) => (
                    <li key={part.type}>
                      <span>{part.type}</span>
                      <strong>{formatMoney(part.amount, result.currency)}</strong>
                    </li>
                  ))}
                </ul>
                <small>
                  Schätzung vom {formatDate(result.feeEstimate.estimatedAt ?? undefined)} bei{" "}
                  {formatMoney(result.feeEstimate.price, result.currency)}
                </small>
              </div>
            )}
            <div className="finance-assumptions">
              {result.assumptions.map((assumption) => (
                <p key={assumption}>
                  <Info size={13} /> {assumption}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}
